import { GeckoBinnedDataBuffer, useCdfRawValues } from 'features/dataLayers'
import { binToMeasurement } from 'features/fde/binningUtils'
import {
  CTP_STD_UNIT,
  normalizeBin,
  parseCtpEntry,
  resolveCtpResolution,
} from 'features/fde/ctpResolution'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoEntry,
  GeckoMeasurement,
  GeckoMeasurementJson,
  RolodexEntryType,
} from 'types'

const ROLODEX_TYPE_CRITICAL_THICKNESS_PROFILE =
  RolodexEntryType.FFS_CRITICAL_THICKNESS_PROFILE

export type CriticalProfilePoint = {
  coords: GeckoComponentCoords
  value: GeckoMeasurement | undefined
  /** Raw (un-normalized, monotonic across a seam) position along the index axis. */
  indexPosition?: GeckoMeasurement | undefined
  /** True when a gap (wrap / dropped no-data hole) precedes this point. */
  breakBefore?: boolean
}

export interface CriticalProfile {
  id: string
  name: string
  points: ReadonlyArray<CriticalProfilePoint>
  cdf_id: string | undefined
  index_axis: 'x' | 'y' | undefined
}

export type CriticalProfiles = {
  profiles: CriticalProfile[]
  cdf: GeckoComponentDataFrame | undefined
}

export function useGetCriticalProfiles(indicationId: string | undefined): {
  data: CriticalProfiles
  isLoading: boolean
  forceRefresh: () => void
} {
  const rolodexSwr = useRolodexEntries(
    useMemo(
      () =>
        indicationId
          ? [
              {
                type: ROLODEX_TYPE_CRITICAL_THICKNESS_PROFILE,
                tags: {
                  indication: indicationId,
                },
              },
            ]
          : [],
      [indicationId]
    )
  )
  const { relevantThicknessCDF: cdf } = useIndicationData()
  const { buffer: binnedDataBuffer, isLoading: cdfDataLoading } =
    useCdfRawValues(cdf)

  const needsCdfValues = rolodexSwr.entries.some(
    (entry) => !Array.isArray(entry.data.thickness)
  )

  return useMemo(
    () => ({
      data: {
        profiles:
          rolodexSwr.entries?.map((entry) => ({
            id: entry.id,
            points: entryToPoints(entry, cdf, binnedDataBuffer),
            cdf_id: entry.tags['component-data-frame'] ?? undefined,
            index_axis: (entry.data['index_axis'] ?? undefined) as
              | 'x'
              | 'y'
              | undefined,
            name: `Profile ${entry.id.substring(0, 4)}`,
          })) ?? [],
        cdf,
      },
      isLoading: rolodexSwr.isLoading || (needsCdfValues && cdfDataLoading),
      forceRefresh: rolodexSwr.forceRefresh,
    }),
    // Le smell: The card draws markup based on this, which
    // invalidates the entries, which means we re-draw the markup,
    // which invalidates the entries and so on. This ugly fix breaks
    // that cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cdf,
      binnedDataBuffer,
      cdfDataLoading,
      needsCdfValues,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      rolodexSwr.entries.map((e) => e.id).join('~'),
      rolodexSwr.forceRefresh,
      rolodexSwr.isLoading,
    ]
  )
}

function parseEntryValue(
  json: GeckoMeasurementJson | null | undefined
): GeckoMeasurement | undefined {
  if (!json || typeof json.num !== 'number' || !Number.isFinite(json.num)) {
    return undefined
  }
  return GeckoMeasurement.CreateFromJSON(json)
}

/**
 * Builds the ordered, gap-annotated points for one CTP entry.
 *
 * Values come from the entry's own `thickness` (same length/order
 * as `critical_path_bins`); pre-contract entries fall back to sampling the
 * display CDF buffer. No point is dropped: a no-data point keeps its slot so
 * the chart can break the line instead of joining its neighbours.
 */
export function entryToPoints(
  entry: GeckoEntry,
  cdf: GeckoComponentDataFrame | undefined,
  data: GeckoBinnedDataBuffer | undefined
): CriticalProfilePoint[] {
  const ctp = parseCtpEntry(entry)
  const resolution = resolveCtpResolution(ctp, cdf)
  if (!resolution || !cdf) return []
  const { binWidth, binHeight, geomStartX, geomStartY } = resolution
  const { bins, thickness, indexIdx, hasWrap, wrapIdx, period } = ctp

  return bins.map((bin, i) => {
    const [binX, binY] = bin
    const breakBefore = i > 0 && bin[indexIdx] - bins[i - 1][indexIdx] > 1

    let value: GeckoMeasurement | undefined
    if (thickness) {
      value = parseEntryValue(thickness[i])
    } else {
      // Legacy entries: sample the display CDF buffer, indexed on the CDF's
      // OWN bin counts.
      const numXBins = cdf.data.binning.x_bin_count
      const numYBins = cdf.data.binning.y_bin_count
      const wbx = hasWrap && wrapIdx === 0 ? normalizeBin(binX, numXBins) : binX
      const wby = hasWrap && wrapIdx === 1 ? normalizeBin(binY, numYBins) : binY
      const dataIdx = wby * numXBins + wbx
      const rawValue =
        data && dataIdx < data.values.length
          ? (data.values.at(dataIdx) ?? NaN)
          : NaN
      value = isNaN(rawValue)
        ? undefined
        : GeckoMeasurement.CreateFromValue(
            rawValue,
            cdf.data.params.measurement_unit
          )
    }

    // `coords` are normalized into the period (legacy markup depends on
    // that); `indexPosition` is the raw monotonic position, which on
    // wrap-enabled components may run past the period for a seam-crossing run.
    const cx = hasWrap && wrapIdx === 0 ? normalizeBin(binX, period) : binX
    const cy = hasWrap && wrapIdx === 1 ? normalizeBin(binY, period) : binY
    const coords = GeckoComponentCoords.CreateFromValues(
      cdf.data.geometry.componentID,
      binToMeasurement(cx, geomStartX, binWidth),
      CTP_STD_UNIT,
      binToMeasurement(cy, geomStartY, binHeight),
      CTP_STD_UNIT
    )
    const indexPosition =
      indexIdx === 0
        ? binToMeasurement(binX, geomStartX, binWidth)
        : binToMeasurement(binY, geomStartY, binHeight)
    return {
      coords,
      value,
      indexPosition: GeckoMeasurement.CreateFromValue(
        indexPosition,
        CTP_STD_UNIT
      ),
      breakBefore,
    }
  })
}
